import React from "react";
import {
  CancelButton,
  FeatureFlagEnabled,
  FloatingDropdownList as DropdownList,
  Loader,
  LoadingIndicator,
  SubmitButton
} from "elements";
import i18n, { LocalKeys, useTranslation } from "utilities/i18n";
import { MemberDetailsProps } from "routes/Admin/Accounts/Members/MemberDetails/MemberDetails";
import { MemberDetailsCard } from "routes/Admin/Accounts/Members/MemberDetails/MemberDetailsCard";
import {
  Grant,
  Role,
  Tenant,
  usePermissionsQuery,
  useUpdateUserInfoMutation,
  useUserByIdQuery
} from "generated/graphql";
import { Layout } from "views/Main/Layouts";
import { Link, NavLink, Redirect, Route, Switch } from "polyrouter";
import { ModalForm, useModalDispatch } from "collections/Modal";
import { Form, Input } from "collections";
import { DISMISS, useNotification } from "collections/Notifications";
import * as yup from "yup";
import { InferType } from "yup";
import ProfileSettings from "./ProfileSettings";
import { useAuth0 } from "@auth0/auth0-react";
import PermissionsCard from "./PermissionsCard";
import UserNotificationCenter from "../Accounts/Notifications/UserNotificationCenter";

export type TenantProps = Pick<Tenant, "id" | "name">;
export type GrantProps = Pick<Grant, "resourceId" | "resourceType"> & {
  roles: Array<Pick<Role, "name" | "tags">>;
};
export type RoleProps = Pick<Role, "name">;
export type RolePropsi18n = Omit<RoleProps, "name"> & { name: LocalKeys };

// IMPORTANT NOTE: Why isn't this inline in the useQuery? Because it causes an infinite loop.....
const userPermContext = {
  notify: {
    i18nKey: "ERROR_LOADING_USER_PERMISSION"
  }
};

export const UserPermissionsLoader: React.FC<{
  id: string;
}> = ({ id }) => {
  const { data, fetching, error } = usePermissionsQuery({
    variables: {
      accessorId: id
    },
    context: userPermContext
  });

  if (fetching) return <LoadingIndicator />;

  if (error) {
    return <></>;
  }

  const tenants: Array<TenantProps> = data?.tenants ?? [];
  const loggedInUserGrants: Array<GrantProps> =
    data?.loggedInUser?.grants ?? [];

  return (
    <PermissionsCard
      tenants={tenants}
      loggedInUserGrants={loggedInUserGrants}
    />
  );
};

export const nameSchema = yup
  .string()
  .required()
  .default("")
  .test(
    "no-blanks",
    i18n.t("ERROR_INVALID_NAME"),
    value => !value.includes(" ")
  );
export const profileViewSchema = yup.object({
  given_name: nameSchema.label(i18n.t("FIRST_NAME")),
  family_name: nameSchema.label(i18n.t("LAST_NAME"))
});
type EditUserState = InferType<typeof profileViewSchema>;

const useUser = () => {
  const { user } = useAuth0();
  const query = useUserByIdQuery({
    variables: {
      id: user ? user.sub : ""
    }
  });
  return { user, ...query };
};

const EditUserModal: React.FC = () => {
  const { t } = useTranslation();
  const [, updateUser] = useUpdateUserInfoMutation();
  const [addNotification] = useNotification();
  const cancel = useModalDispatch();
  const { data, executor: refresh, user } = useUser();
  if (!data?.user?.user) {
    return <></>;
  }

  const profile: MemberDetailsProps = data?.user?.user;

  const initialState: EditUserState = {
    given_name: profile.given_name ?? "",
    family_name: profile.family_name ?? ""
  };

  const submit = async (values: EditUserState) => {
    const resp = await updateUser({
      id: user ? user.sub : "",
      user: {
        ...values,
        name: `${values.given_name} ${values.family_name}`
      }
    });

    if (resp.error) {
      addNotification({
        id: "edit-error",
        type: "error",
        message: t("ERROR_USER_EDIT"),
        dismiss: "USER_ONLY"
      });
    } else {
      addNotification({
        id: "edit-success",
        type: "success",
        message: t("SUCCESSFULLY_EDIT_USER"),
        dismiss: DISMISS.DEFAULT
      });
      setTimeout(() => {
        refresh({
          requestPolicy: "network-only"
        });
        cancel();
      }, 2000);
    }
  };

  return (
    <ModalForm
      title={t("EDIT_MY_INFORMATION")}
      name="EditUser"
      initialValues={initialState}
      validationSchema={profileViewSchema}
      onSubmit={submit}
      validateOnChange
      enableReinitialize
    >
      {({ field, dirty, isSubmitting, isValid }) => (
        <Form>
          <Input {...field.given_name} tabIndex={1} />
          <Input {...field.family_name} tabIndex={2} />
          <div className="btn-container">
            <CancelButton onClick={cancel} />
            <SubmitButton
              isSubmitting={isSubmitting}
              disabled={!dirty || !isValid}
              submittingLabelKey="SAVING"
              labelKey="SAVE_CHANGES"
            />
          </div>
        </Form>
      )}
    </ModalForm>
  );
};

const ProfileView: React.FC = () => {
  const { t } = useTranslation();
  const { data, fetching } = useUser();

  if (!data?.user?.user) {
    return <></>;
  }

  const profile: MemberDetailsProps = data?.user?.user;
  const isLocal =
    !!profile.identities &&
    !profile.identities.some(identity => identity?.isSocial);

  const { Content } = Layout;
  return (
    <Loader loading={fetching}>
      <Content scrolling details>
        <Content.Title>{t("MY_PROFILE")}</Content.Title>
        <Content.ScrollingList>
          <MemberDetailsCard {...profile} />
          {isLocal ? (
            <Link nest to="/edit">
              <button
                className="btn inline"
                type="button"
                data-testid="edit-member-details"
              >
                {t("EDIT_MY_INFORMATION")}
              </button>
            </Link>
          ) : null}
          <UserPermissionsLoader id={profile.user_id} />
        </Content.ScrollingList>
      </Content>
      {isLocal && profile ? (
        <Route nest path="/edit">
          <EditUserModal />
        </Route>
      ) : null}
    </Loader>
  );
};

const ProfileRouter: React.FC = () => {
  const { t } = useTranslation();
  const { data } = useUser();
  if (!data?.user?.user) {
    return <></>;
  }

  const profile: MemberDetailsProps = data?.user?.user;

  const { Sidebar } = Layout;

  return (
    <Layout>
      <Sidebar>
        {profile.picture && (
          <img src={profile.picture} alt={t("MY_PROFILE")} className="avatar" />
        )}
        <FeatureFlagEnabled feature="LENS_LANGUAGE_SELECT">
          <DropdownList
            name="language-select"
            options={[
              {
                text: t("ENGLISH"),
                value: "en"
              },
              {
                text: "Dmhkhrg",
                value: "xx"
              }
            ]}
            value={i18n.language}
            label={t("SELECT_LANGUAGE")}
            onChange={lang => i18n.changeLanguage(lang)}
          />
        </FeatureFlagEnabled>
        <ul className="menu router">
          <li>
            <NavLink nest exact to="/details">
              {t("INFORMATION")}
            </NavLink>
          </li>
          <li>
            <NavLink nest exact to="/settings">
              {t("SETTINGS")}
            </NavLink>
          </li>
          <li>
            <NavLink nest exact to="/notifications">
              {t("NOTIFICATIONS")}
            </NavLink>
          </li>
        </ul>
      </Sidebar>
      <Switch>
        <Route nest path="/details">
          <ProfileView />
        </Route>
        <Route nest path="/settings">
          <ProfileSettings />
        </Route>
        <Route nest path="/notifications">
          <UserNotificationCenter userId={data?.user?.user.user_id} />
        </Route>
        <Route>
          <Redirect nest to="/details" />
        </Route>
      </Switch>
    </Layout>
  );
};

export default ProfileRouter;
